import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { cx } from '@/utils';
import { useLayoutContext } from '@common/Layout/LayoutProvider/LayoutContext';

import { Skeleton } from '@/components/ui/Skeleton';

import Search from '../../../../modules/Search/components/widget/HeaderSearch';
import CustomLink from '../../../common/CustomLink';
import { PageBlockStyles } from '../../../common/Providers/GraphqlProvider/generated';
import MobileCollapsibleMenu from '../../PageNavigationBar/public/MobileCollapsibleMenu';

const LogosHeaderSkeleton = () => {
  return (
    <div className="absolute inset-0">
      <div className="mx-4 flex h-full flex-row items-center justify-between lg:mx-[86px]">
        <div className="flex flex-row items-center">
          <div className="flex flex-row items-center justify-center space-x-2 md:space-x-4">
            <div className="relative h-[35px] w-[35px] md:h-[48px] md:w-[50px]">
              <Skeleton className="h-full w-full bg-tundora/20" />
            </div>
            <div className="h-[48px] w-[1px] bg-dimGray" />
            <div className="relative h-[35px] w-[35px] md:h-[48px] md:w-[50px]">
              <Skeleton className="h-full w-full bg-tundora/20" />
            </div>
            <div className="relative h-[35px] w-[35px] md:h-[48px] md:w-[50px]">
              <Skeleton className="h-full w-full bg-tundora/20" />
            </div>
            <div className="hidden md:block">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-6 w-56 bg-tundora/20" />
                <Skeleton className="h-4 w-40 bg-tundora/20" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-row items-center gap-4">
          <Skeleton className="hidden h-10 w-[280px] bg-tundora/20 md:block" />
          <div className="block lg:hidden">
            <Skeleton className="h-8 w-8 rounded-full bg-tundora/20" />
          </div>
        </div>
      </div>
    </div>
  );
};

const LogosHeader = () => {
  const {
    layout: { headerOrganizationBar, searchBlock },
  } = useLayoutContext();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const logoImageSrc = headerOrganizationBar?.logoImage?.downloadUrl ?? null;
  const adminLogoSrc = headerOrganizationBar?.administrationLogo?.downloadUrl ?? null;
  const otherLogoSrc = headerOrganizationBar?.adminOtherLogo?.downloadUrl ?? null;

  const logoSources = useMemo(
    () => [logoImageSrc, adminLogoSrc, otherLogoSrc].filter(Boolean) as string[],
    [logoImageSrc, adminLogoSrc, otherLogoSrc]
  );

  const logoSourcesKey = useMemo(() => logoSources.join('|'), [logoSources]);
  const loadedSrcsRef = useRef<Set<string>>(new Set());
  const [loadedCount, setLoadedCount] = useState(0);

  useEffect(() => {
    loadedSrcsRef.current.clear();
    setLoadedCount(0);
  }, [logoSourcesKey]);

  const markLoaded = (src: string) => {
    if (!src) return;
    if (loadedSrcsRef.current.has(src)) return;
    loadedSrcsRef.current.add(src);
    setLoadedCount((count) => count + 1);
  };

  const isLayoutLoading = !headerOrganizationBar || !searchBlock;
  const isAnyLogoLoading = logoSources.length > 0 && loadedCount < logoSources.length;
  const shouldShowSkeleton = isLayoutLoading || isAnyLogoLoading;

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const isSearchEnabled = !!(
    searchBlock &&
    !searchBlock.isDisabled &&
    searchBlock.pageBlockStyle === PageBlockStyles.B
  );

  return (
    <>
      <nav
        className=" relative z-base flex w-full flex-row bg-white py-2 md:py-4"
        aria-busy={shouldShowSkeleton}
      >
        {shouldShowSkeleton && <LogosHeaderSkeleton />}

        <div
          className={cx(
            'mx-4 flex w-full flex-row items-center justify-between lg:mx-[86px]',
            shouldShowSkeleton ? 'pointer-events-none opacity-0' : 'opacity-100'
          )}
        >
          <div className="flex flex-row items-center">
            <div className="flex flex-row items-center justify-center space-x-2 md:space-x-4">
              {logoImageSrc && (
                <CustomLink
                  pageLink={headerOrganizationBar?.logoImageLink}
                  className={'relative h-[35px] w-[35px] md:h-[48px] md:w-[50px]'}
                >
                  <Image
                    src={logoImageSrc}
                    alt={'logo'}
                    fill
                    sizes="(max-width: 768px) 35px, 50px"
                    className="object-contain"
                    onLoadingComplete={() => markLoaded(logoImageSrc)}
                    onError={() => markLoaded(logoImageSrc)}
                  />
                </CustomLink>
              )}
              <div className="h-[48px] w-[1px] bg-dimGray" />
              {adminLogoSrc && (
                <CustomLink
                  pageLink={headerOrganizationBar?.adminLogoLink}
                  className={'relative h-[35px] w-[35px] md:h-[48px] md:w-[50px]'}
                >
                  <Image
                    src={adminLogoSrc}
                    alt={'logo'}
                    fill
                    sizes="(max-width: 768px) 35px, 50px"
                    className="object-contain"
                    onLoadingComplete={() => markLoaded(adminLogoSrc)}
                    onError={() => markLoaded(adminLogoSrc)}
                  />
                </CustomLink>
              )}
              {otherLogoSrc && (
                <CustomLink
                  pageLink={headerOrganizationBar?.adminAnotherLink}
                  className={'relative h-[35px] w-[35px] md:h-[48px] md:w-[50px]'}
                >
                  <Image
                    src={otherLogoSrc}
                    alt={'logo'}
                    fill
                    sizes="(max-width: 768px) 35px, 50px"
                    className="object-contain"
                    onLoadingComplete={() => markLoaded(otherLogoSrc)}
                    onError={() => markLoaded(otherLogoSrc)}
                  />
                </CustomLink>
              )}
              <div className={'hidden md:block'}>
                <div className={'headline-h4 line-clamp-1 text-tundora'}>
                  {headerOrganizationBar?.title}
                </div>
                <div className={'headline-h6 line-clamp-1 text-dimGray'}>
                  {headerOrganizationBar?.subtitle}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center ">
            {isSearchEnabled && <Search />}
            <div className={'block lg:hidden'}>
              <button className="relative h-8 w-8 focus:outline-none" onClick={toggleMobileMenu}>
                <span className="sr-only">Toggle menu</span>
                <div className="absolute left-1/2 top-1/2 block w-5 -translate-x-1/2 -translate-y-1/2">
                  <span
                    aria-hidden="true"
                    className={cx(
                      'absolute block h-0.5 w-5 transform bg-current transition duration-500 ease-in-out',
                      isMobileMenuOpen ? 'rotate-45' : '-translate-y-1.5'
                    )}
                  ></span>
                  <span
                    aria-hidden="true"
                    className={cx(
                      'absolute block h-0.5 w-5 transform bg-current transition duration-500 ease-in-out',
                      isMobileMenuOpen ? 'opacity-0' : 'opacity-100'
                    )}
                  ></span>
                  <span
                    aria-hidden="true"
                    className={cx(
                      'absolute block h-0.5 w-5 transform bg-current transition duration-500 ease-in-out',
                      isMobileMenuOpen ? '-rotate-45' : 'translate-y-1.5'
                    )}
                  ></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>
      <MobileCollapsibleMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        headerHeight="142px"
      />
    </>
  );
};

export default LogosHeader;
